<script>
import moment from 'moment-timezone'

const DEFAULT_COUNTDOWN_MS = 10 * 60 * 1000

export default {
  name: 'CopayDepositPanel',
  props: {
    transaction: { type: Object, required: true },
    amount: { type: Number, required: true },
    checking: { type: Boolean, default: false },
    cancelling: { type: Boolean, default: false }
  },
  data () {
    const nowMs = Date.now()
    return {
      nowMs,
      fallbackExpiresAtMs: nowMs + DEFAULT_COUNTDOWN_MS,
      countdownTimer: null
    }
  },
  computed: {
    paymentInfo () {
      return this.transaction.payment_info || {}
    },
    qrImage () {
      const value = String(this.paymentInfo.promptpay_qr || '').trim()
      if (!value) return ''
      if (/^data:image\/(png|jpeg|jpg);base64,[A-Za-z0-9+/=\r\n]+$/i.test(value)) return value
      if (!/^[A-Za-z0-9+/=\r\n]+$/.test(value)) return ''
      return `data:image/png;base64,${value.replace(/\s+/g, '')}`
    },
    displayAmount () {
      return Number(this.paymentInfo.amount || this.amount || 0)
    },
    creditSiteName () {
      const hostname = typeof window === 'undefined' ? '' : String(window.location.hostname || '').toLowerCase()
      if (/fun-?beyond/.test(hostname)) return 'funbeyond'
      if (hostname.includes('vr1game')) return 'vr1game'
      return 'connextcc'
    },
    creditOnlyText () {
      return `ใช้เติมเครดิต ${this.creditSiteName} เท่านั้น`
    },
    expiresAtMs () {
      const timeout = this.paymentInfo.timeout || {}
      const value = timeout.days && timeout.time
        ? `${timeout.days} ${timeout.time}`
        : timeout.date
      if (!value) return this.fallbackExpiresAtMs

      const text = String(value).trim()
      const hasExplicitTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text)
      const parsed = hasExplicitTimezone ? moment.parseZone(text) : moment.utc(text)
      return parsed.isValid() ? parsed.valueOf() : this.fallbackExpiresAtMs
    },
    remainingSeconds () {
      return Math.max(0, Math.ceil((this.expiresAtMs - this.nowMs) / 1000))
    },
    remainingText () {
      const minutes = Math.floor(this.remainingSeconds / 60)
      const seconds = this.remainingSeconds % 60
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
  },
  mounted () {
    this.countdownTimer = window.setInterval(() => {
      this.nowMs = Date.now()
    }, 1000)
  },
  beforeDestroy () {
    if (this.countdownTimer) {
      window.clearInterval(this.countdownTimer)
      this.countdownTimer = null
    }
  }
}
</script>

<template>
  <section class="copay-panel">
    <header class="copay-panel__header">
      <div>
        <strong>Copay</strong>
        <span>สแกน QR เพื่อชำระเงิน</span>
      </div>
      <span class="copay-panel__status">
        <i v-if="checking || cancelling" class="fas fa-spinner fa-spin" aria-hidden="true" />
        {{ cancelling ? 'กำลังยกเลิก' : (checking ? 'กำลังตรวจสอบ' : 'รอชำระเงิน') }}
      </span>
    </header>

    <div class="copay-panel__body">
      <div class="copay-panel__countdown" role="timer" aria-atomic="true">
        <span>กรุณาชำระรายการภายใน</span>
        <strong>{{ remainingText }}</strong>
      </div>

      <div v-if="qrImage" class="copay-panel__qr-wrap">
        <div class="copay-panel__qr-frame">
          <img class="copay-panel__qr" :src="qrImage" alt="Copay PromptPay QR Code">
          <div class="copay-panel__qr-watermark" role="note">
            {{ creditOnlyText }}
          </div>
        </div>
      </div>

      <div class="copay-panel__amount">
        <small>จำนวนเงิน</small>
        <strong>{{ displayAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} บาท</strong>
      </div>
    </div>

    <footer class="copay-panel__actions">
      <button type="button" class="copay-panel__cancel" :disabled="checking || cancelling" @click="$emit('cancel')">
        <i v-if="cancelling" class="fas fa-spinner fa-spin" aria-hidden="true" />
        {{ cancelling ? 'กำลังยกเลิกรายการ...' : 'ยกเลิกรายการเดิมและสร้างรายการใหม่' }}
      </button>
      <button type="button" class="copay-panel__close" data-dismiss="modal" :disabled="cancelling">
        ปิดหน้าต่าง
      </button>
    </footer>
  </section>
</template>

<style scoped>
.copay-panel { display: flex; flex-direction: column; width: calc(100% - 30px); max-height: calc(100vh - 70px); margin: 16px 15px 22px; overflow: hidden; border-radius: 16px; background: #0f172a; color: #fff; font-family: 'Kanit', sans-serif; box-shadow: 0 12px 30px rgba(0, 0, 0, 0.28); }
.copay-panel__header { display: flex; flex: 0 0 auto; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 48px 14px 16px; background: #111827; }
.copay-panel__header > div { display: flex; flex-direction: column; }
.copay-panel__header strong { color: #67e8f9; font-size: 18px; }
.copay-panel__header span { color: #cbd5e1; font-size: 12px; }
.copay-panel__status { white-space: nowrap; }
.copay-panel__status i { margin-right: 5px; color: #60a5fa; }
.copay-panel__body { display: flex; flex: 1 1 auto; flex-direction: column; align-items: center; justify-content: center; gap: 14px; overflow-y: auto; padding: 18px; background: #f8fafc; color: #0f172a; }
.copay-panel__countdown { display: flex; align-items: center; justify-content: center; gap: 8px; width: min(100%, 520px); padding: 10px 14px; border: 1px solid #fecaca; border-radius: 10px; background: #fff; color: #475569; font-size: 14px; }
.copay-panel__countdown strong { color: #dc2626; font-size: 22px; font-variant-numeric: tabular-nums; line-height: 1; }
.copay-panel__amount { display: flex; flex-direction: column; align-items: center; }
.copay-panel__amount small { color: #64748b; }
.copay-panel__amount strong { color: #0f766e; font-size: 28px; }
.copay-panel__qr-wrap { display: flex; flex-direction: column; align-items: center; margin: 0 auto; }
.copay-panel__qr-frame { position: relative; isolation: isolate; width: min(250px, 68vw); }
.copay-panel__qr { display: block; width: 100%; height: auto; padding: 8px; border: 1px solid #cbd5e1; border-radius: 12px; background: #fff; }
.copay-panel__qr-watermark { position: absolute; top: 50%; left: 50%; z-index: 2; padding: 4px 5px; transform: translate(-50%, -50%) rotate(-3deg); color: #dc2626; font-size: clamp(11px, 2.2vw, 15px); font-weight: 700; line-height: 1.2; text-align: center; text-shadow: -1px -1px 0 rgba(255, 255, 255, 0.98), 1px -1px 0 rgba(255, 255, 255, 0.98), -1px 1px 0 rgba(255, 255, 255, 0.98), 1px 1px 0 rgba(255, 255, 255, 0.98), 0 0 7px #fff; white-space: nowrap; pointer-events: none; user-select: none; }
.copay-panel__actions { display: grid; flex: 0 0 auto; gap: 8px; padding: 10px 12px; background: #111827; }
.copay-panel__cancel,
.copay-panel__close { width: 100%; padding: 10px 12px; border: 0; border-radius: 9px; font-size: 13px; font-weight: 600; text-align: center; }
.copay-panel__cancel { background: #fee2e2; color: #b91c1c; }
.copay-panel__cancel i { margin-right: 6px; }
.copay-panel__cancel:disabled,
.copay-panel__close:disabled { cursor: wait; opacity: 0.65; }
.copay-panel__close { background: #e2e8f0; color: #334155; }
@media (max-width: 991.98px) {
  .copay-panel { width: 100vw; height: 100vh; height: 100dvh; max-height: none; margin: 0; border-radius: 0; }
  .copay-panel__header { padding: max(9px, env(safe-area-inset-top)) 42px 9px 14px; }
  .copay-panel__header strong { font-size: 16px; }
  .copay-panel__header span { font-size: 11px; }
  .copay-panel__body { gap: 9px; padding: 10px 14px; }
  .copay-panel__countdown { padding: 8px 12px; font-size: 13px; }
  .copay-panel__countdown strong { font-size: 20px; }
  .copay-panel__amount strong { font-size: 26px; }
  .copay-panel__qr-frame { width: min(230px, 62vw); }
  .copay-panel__qr { padding: 6px; border-radius: 9px; }
  .copay-panel__actions { gap: 6px; padding: 8px 10px max(8px, env(safe-area-inset-bottom)); }
  .copay-panel__cancel,
  .copay-panel__close { padding: 8px 10px; }
}
</style>
